import React, { useCallback, useState } from 'react';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { Formik, Field, Form, FormikHelpers } from 'formik';
import { Modal as BootstrapModal, Button } from 'react-bootstrap';
import * as Yup from 'yup';

import { FormGroup, TextboxField, Modal, SelectDropdownField } from '@tradingblock/components';
import { useStateSelector } from '../../../data/global/dataSelectors';
import { AccountManagementDataActions } from '../../../data/global/actions/AccountManagementActions';
import {
  AllYearsEmployed,
  EmploymentTypes,
  AllStates,
  EmployInfoUpdateValueProps,
  getGroupedCountryOptions,
  Country,
  EmploymentType,
  EmployerAddress,
} from '@tradingblock/types';
import { useClearer } from '../../../hooks/useClearer';

export const EmployInfoUpdate: React.FunctionComponent<{
  show?: boolean;
  toggleModal: () => void;
  accountHolderIndex: number;
}> = ({ show, toggleModal, accountHolderIndex }) => {
  const clearer = useClearer();
  const dispatch = useDispatch();

  const accountId = useStateSelector(s => s.accounts.account && s.accounts.account.AccountId);
  const { accountHolders } = useStateSelector(s => s.accountManagement.accountManagementDetails.details);

  const [isCountryUs, setIsCountryUs] = useState<boolean | null>(null);
  const [isEmployed, setIsEmployed] = useState<boolean | null>(null);

  const user = accountHolders[accountHolderIndex];
  const initialEmployInfoUpdateValues: EmployInfoUpdateValueProps = {
    employmentType: user ? user.employmentType : '',
    employer: user ? user.employer : '',
    jobPosition: user ? user.jobPosition : '',
    yearsEmployed: user ? user.yearsEmployed : '',
    employerAddress: {
      address1: (user && user.employerAddress && user.employerAddress.address1) || '',
      address2: (user && user.employerAddress && user.employerAddress.address2) || '',
      country: (user && user.employerAddress && user.employerAddress.country) || '',
      state: (user && user.employerAddress && user.employerAddress.state) || '',
      city: (user && user.employerAddress && user.employerAddress.city) || '',
      postalCode: (user && user.employerAddress && user.employerAddress.postalCode) || '',
    },
  };

  const onSubmit = useCallback(
    (values: EmployInfoUpdateValueProps, { resetForm }: FormikHelpers<EmployInfoUpdateValueProps>) => {
      const { employmentType, employer, jobPosition, yearsEmployed, employerAddress } = values;
      const { address1, address2, country, state, city, postalCode } = employerAddress || {};
      let updateValues = {};

      switch (employmentType) {
        case 'Employed':
          updateValues = {
            item: 'Employment',
            accountHolder: {
              employmentType,
              employer,
              jobPosition,
              yearsEmployed,
              employerAddress: {
                address1,
                address2,
                country,
                state,
                city,
                postalCode,
              },
            },
            holder: accountHolderIndex,
          };
          break;
        case 'Student':
        case 'Unemployed':
        case 'Retired':
          updateValues = {
            item: 'Employment',
            accountHolder: {
              employmentType,
              employer: '',
              jobPosition: '',
              yearsEmployed: '',
              employerAddress: {
                address1: '',
                address2: '',
                country: 'USA',
                state: 'IL',
                city: '',
                postalCode: 99999,
              },
            },
            holder: accountHolderIndex,
          };
          break;
        default:
          break;
      }

      dispatch(
        AccountManagementDataActions.requestMakeAccountUpdate({
          accountId: accountId,
          request: updateValues,
        })
      );
      resetForm();
      toggleModal();
    },
    [toggleModal, accountId, accountHolderIndex, dispatch]
  );

  const formFieldValidationSchema = Yup.string()
    .nullable()
    .trim()
    .required('This field is required.');

  return (
    <Modal setShow={() => toggleModal()} show={show}>
      <Formik
        validateOnBlur
        validateOnChange
        initialValues={initialEmployInfoUpdateValues}
        onSubmit={onSubmit}
        validationSchema={Yup.object<EmployInfoUpdateValueProps>().shape({
          employmentType: formFieldValidationSchema,
          ...(isEmployed && {
            employerAddress: Yup.object<EmployerAddress>().shape({
              address1: formFieldValidationSchema,
              city: formFieldValidationSchema,
              country: formFieldValidationSchema,
              ...((isCountryUs || isCountryUs === null) && {
                postalCode: formFieldValidationSchema,
                state: formFieldValidationSchema,
              }),
            }),
            yearsEmployed: formFieldValidationSchema,
            jobPosition: formFieldValidationSchema,
            employer: formFieldValidationSchema,
          }),
        })}
      >
        {({ values, resetForm }) => {
          setIsEmployed(values.employmentType === EmploymentType.Employed);

          if (!values.employerAddress || (values.employerAddress && !values.employerAddress.country)) {
            setIsCountryUs(null);
          } else if (values.employerAddress && values.employerAddress.country === Country.UnitedStatesOfAmerica) {
            setIsCountryUs(true);
          } else if (values.employerAddress && values.employerAddress.country !== Country.UnitedStatesOfAmerica) {
            setIsCountryUs(false);
          }

          return (
            <Form translate={undefined}>
              <BootstrapModal.Header closeButton>
                <BootstrapModal.Title>Employment Information Update</BootstrapModal.Title>
              </BootstrapModal.Header>
              <BootstrapModal.Body className="update-employment-info-modal-body">
                <FormGroup>
                  <Field
                    component={SelectDropdownField}
                    isSearchable={false}
                    name="employmentType"
                    id="employmentType"
                    options={EmploymentTypes}
                    label="Employment Type"
                  />
                </FormGroup>
                <p className="txt-sm mute">Please provide your employment status and any applicable information.</p>
                {isEmployed && (
                  <>
                    <label>Employer Address</label>
                    <FormGroup>
                      <Field
                        component={SelectDropdownField}
                        name="employerAddress.country"
                        id="employerAddress.country"
                        label="Country"
                        options={getGroupedCountryOptions(clearer)}
                        optionsDefaultLabelId="country"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Field
                        component={TextboxField}
                        name="employerAddress.address1"
                        id="employerAddress.address1"
                        label="Address 1"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Field
                        component={TextboxField}
                        name="employerAddress.address2"
                        id="employerAddress.address2"
                        label="Address 2"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Field
                        component={TextboxField}
                        name="employerAddress.city"
                        id="employerAddress.city"
                        label="City"
                      />
                    </FormGroup>
                    {(isCountryUs || isCountryUs === null) && (
                      <FormGroup>
                        <Field
                          component={SelectDropdownField}
                          name="employerAddress.state"
                          id="employerAddress.state"
                          label="State"
                          options={AllStates}
                          optionsDefaultLabelId="state"
                        />
                      </FormGroup>
                    )}
                    {!isCountryUs && isCountryUs !== null && (
                      <FormGroup>
                        <Field
                          component={TextboxField}
                          name="employerAddress.state"
                          id="employerAddress.state"
                          label="Province"
                        />
                      </FormGroup>
                    )}
                    <FormGroup>
                      <Field
                        component={TextboxField}
                        name="employerAddress.postalCode"
                        id="employerAddress.postalCode"
                        label="Postal Code"
                      />
                    </FormGroup>
                    <label>Employer Info</label>
                    <FormGroup>
                      <Field component={TextboxField} id="employer" name="employer" label="Employer Name" />
                    </FormGroup>
                    <FormGroup>
                      <Field component={TextboxField} id="jobPosition" name="jobPosition" label="Job Title" />
                    </FormGroup>
                    <FormGroup>
                      <Field
                        component={SelectDropdownField}
                        isSearchable={false}
                        id="yearsEmployed"
                        name="yearsEmployed"
                        options={AllYearsEmployed}
                        label="Years Employed"
                      />
                    </FormGroup>
                  </>
                )}
              </BootstrapModal.Body>
              <BootstrapModal.Footer className="modal-footer-justified">
                <Button
                  variant="secondary"
                  onClick={() => {
                    resetForm();
                    toggleModal();
                  }}
                >
                  Cancel
                </Button>
                <Button variant="primary" type="submit">
                  Submit
                </Button>
              </BootstrapModal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
